import React from "react";
import { hijriYears, daysInHijriMonth } from "@/lib/hijri";
import { useT, HIJRI_MONTHS_I18N } from "@/lib/i18n";

/**
 * Hijri date picker using three dropdowns (year, month, day).
 * value: {y, m, d} | null
 * onChange: ({y, m, d} | null) => void
 */
export default function HijriDatePicker({ value, onChange, label, testid }) {
  const { t, lang } = useT();
  const v = value || {};
  const years = hijriYears();
  const months = HIJRI_MONTHS_I18N[lang] || HIJRI_MONTHS_I18N.ar;
  const maxDay = v.y && v.m ? daysInHijriMonth(Number(v.y), Number(v.m)) : 30;
  const days = Array.from({ length: maxDay }, (_, i) => i + 1);

  const update = (field, raw) => {
    const next = { ...v };
    if (raw === "") {
      next[field] = undefined;
    } else {
      next[field] = Number(raw);
    }
    if (!next.y && !next.m && !next.d) {
      onChange(null);
      return;
    }
    onChange(next);
  };

  const clear = () => onChange(null);

  return (
    <div>
      {label && <label className="inp-label">{label}</label>}
      <div className="flex gap-2 items-center">
        <select
          className="inp"
          value={v.d || ""}
          onChange={(e) => update("d", e.target.value)}
          data-testid={testid ? `${testid}-day` : undefined}
        >
          <option value="">{t("hijri_day")}</option>
          {days.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <select
          className="inp"
          value={v.m || ""}
          onChange={(e) => update("m", e.target.value)}
          data-testid={testid ? `${testid}-month` : undefined}
        >
          <option value="">{t("hijri_month")}</option>
          {months.map((name, idx) => (
            <option key={idx + 1} value={idx + 1}>{name} - {idx + 1}</option>
          ))}
        </select>
        <select
          className="inp"
          value={v.y || ""}
          onChange={(e) => update("y", e.target.value)}
          data-testid={testid ? `${testid}-year` : undefined}
        >
          <option value="">{t("hijri_year")}</option>
          {years.map((y) => (
            <option key={y} value={y}>{y}{t("hijri_year_suffix")}</option>
          ))}
        </select>
        {(v.y || v.m || v.d) && (
          <button
            type="button"
            onClick={clear}
            className="btn btn-ghost"
            title={t("clear_date")}
            data-testid={testid ? `${testid}-clear` : undefined}
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}
